/**
 * Resolve nível efetivo (userAccessMap produzido por /user-route-levels/me)
 * igual ao antes feito diretamente no router guard.
 */

const SALDO_PANEL_PATH = '/logistica/saldos';
const SALDO_SCOPE_PATHS = [
    '/logistica/saldos/via',
    '/logistica/saldos/via/valores',
    '/logistica/saldos/siminova',
    '/logistica/saldos/siminova/valores',
    '/logistica/saldos/exportar',
    '/logistica/saldos/relatorios/via',
    '/logistica/saldos/relatorios/siminova',
    '/logistica/saldos/admin/sync',
];

// Extrato Bancário: a página geral herda o maior nível entre as rotas
// de escopo por empresa (via/sim) — quem tem acesso a UMA empresa já
// consegue abrir o painel (o switcher/back-end limitam ao escopo).
const EXTRATO_PANEL_PATH = '/financeiro/relatorios/extratos';
const EXTRATO_SCOPE_PATHS = [
    '/financeiro/relatorios/extratos/via',
    '/financeiro/relatorios/extratos/sim',
];

// Saldos por Endereço (MATA226): mesma herança por empresa (via/sim).
const SALDO_END_PANEL_PATH = '/logistica/relatorios/saldos-endereco';
const SALDO_END_SCOPE_PATHS = [
    '/logistica/relatorios/saldos-endereco/via',
    '/logistica/relatorios/saldos-endereco/sim',
];

// Painéis cuja página geral herda o maior nível entre as rotas de escopo por
// empresa (via/sim) — mesma regra dos blocos acima, em forma de mapa para não
// ganhar um `if` novo a cada relatório com switcher.
// Posição de Clientes/Fornecedores (FINR340/350) e Relatório de Compras.
const PAINEIS_ESCOPO_EMPRESA: Record<string, string[]> = {
    '/financeiro/relatorios/fiscal-tss': [
        '/financeiro/relatorios/fiscal-tss/via',
        '/financeiro/relatorios/fiscal-tss/sim',
    ],
    '/financeiro/relatorios/posicao-clientes': [
        '/financeiro/relatorios/posicao-clientes/via',
        '/financeiro/relatorios/posicao-clientes/sim',
    ],
    '/financeiro/relatorios/posicao-fornecedores': [
        '/financeiro/relatorios/posicao-fornecedores/via',
        '/financeiro/relatorios/posicao-fornecedores/sim',
    ],
    '/compras/relatorios/pedidos': [
        '/compras/relatorios/pedidos/via',
        '/compras/relatorios/pedidos/sim',
    ],
};

const ACCESS_RANK: Record<string, number> = { none: 0, member: 1, editor: 2, admin: 3 };

function maxAccessLevel(levels: string[]): string | undefined {
    let best: string | undefined;
    for (const lvl of levels) {
        if (best === undefined || (ACCESS_RANK[lvl] ?? 0) > (ACCESS_RANK[best] ?? 0)) {
            best = lvl;
        }
    }
    return best;
}

/** Maior nível entre as rotas de escopo (via/sim) de um painel com switcher. */
function nivelHerdadoDosEscopos(
    painel: string,
    userAccessMap: Map<string, string>
): string | undefined {
    const escopos = PAINEIS_ESCOPO_EMPRESA[painel];
    if (!escopos) return undefined;
    const inherited = escopos
        .map((p) => userAccessMap.get(p))
        .filter((lvl): lvl is string => lvl !== undefined);
    return maxAccessLevel(inherited);
}

export function normalizeRoutePath(routePath: string): string {
    return routePath.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
}

/**
 * Telas do Master Loja com sub-caminhos de detalhe/criação (`/nova`, `/novo`,
 * `/:id`). `prefixo` é o caminho cujos filhos herdam; `rota` é a permissão da
 * tela. Produtos é a exceção de nome: a tela vive na raiz do módulo, mas o
 * detalhe fica em `/produtos/:id` — por isso a raiz só cobre `/produtos/…`.
 * Lista explícita de propósito: um prefixo genérico do módulo devolveria a
 * herança ampla que a permissão por tela existe para eliminar — e rotas
 * próprias como `/publicacao`, `/precos`, `/expedicao` e `/categorias` NUNCA
 * caem na regra da raiz (a raiz só casa `/produtos/…`).
 */
export const TELAS_MASTER_LOJA: readonly { prefixo: string; rota: string }[] = [
    { prefixo: '/ecommerce/master-loja/produtos', rota: '/ecommerce/master-loja' },
    { prefixo: '/ecommerce/master-loja/promocoes', rota: '/ecommerce/master-loja/promocoes' },
    { prefixo: '/ecommerce/master-loja/campanhas', rota: '/ecommerce/master-loja/campanhas' },
    { prefixo: '/ecommerce/master-loja/banners', rota: '/ecommerce/master-loja/banners' },
    // O detalhe da expedição é drawer, mas `/expedicao/:numero` existe na tabela.
    { prefixo: '/ecommerce/master-loja/expedicao', rota: '/ecommerce/master-loja/expedicao' },
];

export function resolveUserRouteAccessLevel(
    routePath: string,
    userAccessMap: Map<string, string>
): string | undefined {
    const pathWithoutQuery = normalizeRoutePath(routePath);

    // Master Loja: sub-caminho de detalhe/criação resolve pela TELA-MÃE, ANTES do
    // match exato. As sub-rotas (`/promocoes/nova`, `/produtos/:id`…) existem na
    // tabela `routes` com `requires_admin_master = 1` (ruído do syncFrontendRoutes)
    // e `/user-route-levels/me` devolve toda rota cadastrada: chegariam aqui como
    // exato `none` e quem tem `/promocoes` editor não abriria `/promocoes/nova`.
    // O backend já checa a tela-mãe. Mesmo raciocínio do carve-out via/sim abaixo.
    const telaMasterLoja = TELAS_MASTER_LOJA.find(({ prefixo }) => pathWithoutQuery.startsWith(`${prefixo}/`));
    if (telaMasterLoja) return userAccessMap.get(telaMasterLoja.rota);

    const exact = userAccessMap.get(pathWithoutQuery);
    if (exact !== undefined) {
        // ⚠️ Painel com switcher cuja rota GERAL é RESTRITA (Documentos Fiscais):
        // `/user-route-levels/me` devolve TODA rota cadastrada, e para a geral sem
        // concessão o default de rota restrita é `none` — que chega aqui como
        // match exato e venceria a herança dos escopos lá embaixo. Resultado: quem
        // tem concessão só em `/via` passava a não abrir a tela nem ver o menu.
        // O `none` padrão da geral não é decisão de ninguém; a concessão no escopo
        // é. Então só aqui o exato cede para o maior nível herdado dos escopos.
        // Concessão explícita na geral (member/editor/admin) continua valendo.
        if (exact === 'none' && PAINEIS_ESCOPO_EMPRESA[pathWithoutQuery]) {
            const herdado = nivelHerdadoDosEscopos(pathWithoutQuery, userAccessMap);
            if (herdado !== undefined) return herdado;
        }
        return exact;
    }

    for (const [pattern, level] of userAccessMap.entries()) {
        if (pattern.includes(':')) {
            const regex = new RegExp(`^${pattern.replace(/:[^/]+/g, '[^/]+')}$`);
            if (regex.test(pathWithoutQuery)) {
                return level;
            }
        }
    }

    // Assistente: cada conversa é uma sub-rota (/assistente/123) e herda o nível
    // da rota base. Cadastrar rota por conversa é impossível — elas nascem em
    // runtime, uma por chat aberto. O backend enforça TODOS os endpoints de
    // /api/assistente sob a única permissão `/assistente`.
    //
    // A herança casa SÓ id numérico, de propósito: `/assistente/admin` é o painel
    // de métricas, rota própria de nível `admin` (migration 182). Um prefixo
    // genérico daria a ele o `member` de todo usuário do chat.
    if (pathWithoutQuery === '/assistente' || /^\/assistente\/\d+$/.test(pathWithoutQuery)) {
        const baseLevel = userAccessMap.get('/assistente');
        if (baseLevel !== undefined) {
            return baseLevel;
        }
    }

    // Faturas TI: rota de permissão ÚNICA (/tecnologia/faturas-ti, migration 353) — o
    // backend enforça TODO /api/faturas-ti sob ela. Configurações e cadastro de conta
    // (nova / editar por id numérico) herdam o nível. Regex FECHADA de propósito: nenhuma
    // outra sub-rota (ex.: contas/abc) herda por prefixo.
    if (
        pathWithoutQuery === '/tecnologia/faturas-ti' ||
        /^\/tecnologia\/faturas-ti\/(configuracoes|contas\/(nova|\d+))$/.test(pathWithoutQuery)
    ) {
        const baseLevel = userAccessMap.get('/tecnologia/faturas-ti');
        if (baseLevel !== undefined) {
            return baseLevel;
        }
    }

    // Auditoria de Comodato: rota de permissão ÚNICA (/auditoria-comodato, migration 359) — o backend
    // enforça TODO /api/auditoria-comodato sob ela. Regex FECHADA: só as telas do módulo herdam.
    if (
        pathWithoutQuery === '/auditoria-comodato' ||
        /^\/auditoria-comodato\/(escolas|escolas\/\d+|itens|consolidado|aparelhos)$/.test(pathWithoutQuery)
    ) {
        const baseLevel = userAccessMap.get('/auditoria-comodato');
        if (baseLevel !== undefined) {
            return baseLevel;
        }
    }

    // Controle de Acesso: permissão POR SEÇÃO (migration 371) — Cadastro, Agenda, Device e Log são
    // quatro rotas de permissão com concessão própria; NENHUMA herda da outra nem da raiz.

    // Inovação: rota de permissão ÚNICA (/inovacao, migration 193) — as telas
    // (nova, minhas, ideias, detalhe/:id, :id/editar) herdam o nível dela.
    // Regex FECHADA de propósito (lição do /assistente/admin): uma futura
    // sub-rota administrativa não pode herdar o `member` de todo mundo.
    if (
        pathWithoutQuery === '/inovacao' ||
        /^\/inovacao\/(nova|minhas|ideias|\d+(\/editar)?)$/.test(pathWithoutQuery)
    ) {
        const baseLevel = userAccessMap.get('/inovacao');
        if (baseLevel !== undefined) {
            return baseLevel;
        }
    }

    if (pathWithoutQuery === '/policies' || /^\/policies\/\d+$/.test(pathWithoutQuery)) {
        const baseLevel = userAccessMap.get('/policies');
        if (baseLevel !== undefined) {
            return baseLevel;
        }
    }

    // Wiki: a LEITURA do módulo é pública para qualquer autenticado — não exige
    // cadastro por usuário nas Rotas. Toda navegação sob `/wiki` é liberada
    // (`member`); a ESCRITA de artigos e a GESTÃO DE ACESSOS são controladas pela
    // permissão INTERNA do Wiki (memberships por departamento + editores de
    // acessos via rota `/wiki/admin/acessos`), enforce no backend e nos botões.
    // A rota `/wiki/admin/acessos` existe nas Rotas apenas para designar editores
    // de acessos (visibilidade de menu); a tela em si trata quem pode gerenciar.
    if (pathWithoutQuery === '/wiki' || pathWithoutQuery.startsWith('/wiki/')) {
        return 'member';
    }

    // Painel principal herda o maior nível entre as rotas-fantasma de escopo
    if (pathWithoutQuery === SALDO_PANEL_PATH) {
        const inherited = SALDO_SCOPE_PATHS
            .map((p) => userAccessMap.get(p))
            .filter((lvl): lvl is string => lvl !== undefined);
        const best = maxAccessLevel(inherited);
        if (best !== undefined) return best;
    }

    // Extrato Bancário: página geral herda o maior nível dos escopos via/sim
    if (pathWithoutQuery === EXTRATO_PANEL_PATH) {
        const inherited = EXTRATO_SCOPE_PATHS
            .map((p) => userAccessMap.get(p))
            .filter((lvl): lvl is string => lvl !== undefined);
        const best = maxAccessLevel(inherited);
        if (best !== undefined) return best;
    }

    // Saldos por Endereço: página geral herda o maior nível dos escopos via/sim
    if (pathWithoutQuery === SALDO_END_PANEL_PATH) {
        const inherited = SALDO_END_SCOPE_PATHS
            .map((p) => userAccessMap.get(p))
            .filter((lvl): lvl is string => lvl !== undefined);
        const best = maxAccessLevel(inherited);
        if (best !== undefined) return best;
    }

    // Painéis com switcher de empresa: a geral herda o maior nível via/sim
    if (PAINEIS_ESCOPO_EMPRESA[pathWithoutQuery]) {
        const best = nivelHerdadoDosEscopos(pathWithoutQuery, userAccessMap);
        if (best !== undefined) return best;
    }

    // Preços são exceção sensível: não herdam a permissão ampla do módulo.
    // Exigem concessão explícita no Gerenciamento de Usuários.
    //
    // A regra continua valendo depois de a TELA de preços sair: o caminho segue
    // sendo o gate da alteração de preço (consultado por `useUserPermissions` na
    // aba Variantes), e sem esta linha ele cairia na herança logo abaixo — que
    // daria a qualquer editor do módulo o que a concessão existe para restringir.
    if (pathWithoutQuery === '/ecommerce/master-loja/precos') {
        return userAccessMap.get(pathWithoutQuery);
    }

    // Master Loja: permissão POR TELA. Conceder `/ecommerce/master-loja` (Produtos)
    // NÃO libera Categorias, Pedidos, Promoções… — cada tela tem a sua rota, e o
    // backend exige a rota da tela. Os sub-caminhos de detalhe/criação já foram
    // resolvidos pela tela-mãe no topo da função (TELAS_MASTER_LOJA).

    return undefined;
}

/** Paths sob `/permissions`: o guard do router ignora `none` aqui para Admin Master; no menu, o bypass de `effective === 'none'` aplica-se apenas a Admin Master (`menu.ts`). */
export function isPermissionsConfigurationPath(routePath: string): boolean {
    const p = normalizeRoutePath(routePath);
    return p === '/permissions' || p.startsWith('/permissions/');
}
